import { motion, useReducedMotion } from "framer-motion";
import { Check, CreditCard, Lock } from "lucide-react";

const POINTS = [
    "Tarjetas de crédito y débito, con autenticación 3D Secure.",
    "El cobro se procesa de forma segura; los datos de la tarjeta nunca pasan por tu tienda.",
    "Cada pedido queda registrado con su monto, comisión y estado de pago.",
];

export default function PaymentsFeature() {
    const reduce = useReducedMotion();
    const enter = {
        initial: reduce ? false : { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" },
        transition: { duration: reduce ? 0 : 0.55, ease: "easeOut" },
    };

    return (
        <section
            data-testid="home-payments"
            className="py-20 sm:py-28 lg:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    {/* Texto */}
                    <motion.div {...enter}>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                            Pagos en línea
                        </p>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] leading-[1.02] text-[#1A1A1A] text-balance">
                            Cobra con tarjeta desde el primer día
                        </h2>
                        <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">
                            No necesitas terminal, contrato con el banco ni
                            trámites. Activamos los pagos por ti y tus clientes
                            pagan en línea sin salir de tu tienda.
                        </p>
                        <ul className="mt-8 space-y-4">
                            {POINTS.map((p) => (
                                <li key={p} className="flex gap-3 text-[15px] text-[#4A4A4A] leading-relaxed">
                                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Mockup de checkout */}
                    <motion.div
                        {...enter}
                        transition={{ ...enter.transition, delay: reduce ? 0 : 0.12 }}
                        role="img"
                        aria-label="Vista previa del checkout de una tienda Tiendify con el formulario de pago con tarjeta."
                        className="relative"
                    >
                        <div className="rounded-3xl border border-zinc-200 bg-white shadow-xl shadow-zinc-900/5 p-6 sm:p-8 max-w-md mx-auto lg:mx-0 lg:ml-auto">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold text-[#1A1A1A]">
                                    Pagar pedido
                                </p>
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                                    <Lock className="w-3 h-3" aria-hidden="true" />
                                    Seguro
                                </span>
                            </div>

                            <div className="mt-6 space-y-3">
                                <div>
                                    <p className="text-[11px] font-medium text-zinc-500 mb-1.5">
                                        Número de tarjeta
                                    </p>
                                    <div className="h-11 rounded-xl border border-zinc-200 bg-white flex items-center px-3.5 gap-2">
                                        <CreditCard className="w-4 h-4 text-zinc-400" aria-hidden="true" />
                                        <span className="text-sm text-zinc-700 tabular-nums">
                                            4242 4242 4242 4242
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {["12 / 28", "•••"].map((v, i) => (
                                        <div key={i}>
                                            <p className="text-[11px] font-medium text-zinc-500 mb-1.5">
                                                {i === 0 ? "Vencimiento" : "CVC"}
                                            </p>
                                            <div className="h-11 rounded-xl border border-zinc-200 bg-white flex items-center px-3.5">
                                                <span className="text-sm text-zinc-700 tabular-nums">
                                                    {v}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6 pt-5 border-t border-zinc-200 space-y-2">
                                <Row label="Productos" value="$1,150.00" />
                                <Row label="Envío" value="$140.00" />
                                <div className="flex items-center justify-between pt-2">
                                    <span className="text-sm font-semibold text-[#1A1A1A]">
                                        Total
                                    </span>
                                    <span className="text-lg font-bold text-[#1A1A1A] tabular-nums">
                                        $1,290.00
                                    </span>
                                </div>
                            </div>

                            <div className="mt-5 h-12 rounded-full bg-primary grid place-items-center">
                                <span className="text-sm font-semibold text-primary-foreground">
                                    Pagar $1,290.00
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">{label}</span>
            <span className="text-zinc-700 tabular-nums">{value}</span>
        </div>
    );
}
